import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import BarChart from '@cloudscape-design/components/bar-chart';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import { useNavigate } from 'react-router';
import type {
  ComputeCashflowResult,
  ComputeFinanceBalanceResult,
  FinanceAccount,
  FinanceAccountBalance,
  FinanceEntry,
} from '@etiquetachef/types';
import { subscribeFinanceAccounts } from '@/lib/firestoreFinance';
import {
  computeCashflowCallable,
  computeFinanceBalanceCallable,
  searchFinanceEntriesCallable,
} from '@/lib/callables';
import { formatCurrencyCents } from '@/features/dashboard-finance/formatters';
import {
  SignedCents,
  TagBreakdownTable,
} from '@/features/finance-analysis/financeAnalysisShared';
import { serverNow } from '@etiquetachef/ui-shared/online';

const DAY = 86_400_000;

/** Chave `AAAA-MM` do mês corrente em UTC (casa com `periodKey` do cashflow). */
function currentMonthKey(now: number): string {
  const d = new Date(now);
  return `${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Runway em meses: saldo ÷ queima média mensal (só se há queima líquida). */
function runwayMonths(balanceCents: number, buckets: ComputeCashflowResult['buckets'], currency: string): number | null {
  const nets = buckets
    .filter((b) => b.totalsByCurrency[currency] !== undefined)
    .map((b) => b.totalsByCurrency[currency]?.netCents ?? 0);
  if (nets.length === 0) return null;
  const avg = nets.reduce((s, n) => s + n, 0) / nets.length;
  if (avg >= 0 || balanceCents <= 0) return null;
  return balanceCents / -avg;
}

/**
 * Tela `/finance`: visão geral do razão interno — saldo atual, mês corrente,
 * runway, saldo por conta, fluxo por tag e últimos lançamentos.
 *
 * Origem: #76 — 2026-06-21 23:20 +0200 (Financeiro — telas Visão geral,
 * Caixa e Balanço).
 *
 * Contrato: no mount, três callables em paralelo (`Promise.all`; qualquer
 * falha → `Alert` de erro): `computeFinanceBalance({})` (posição de hoje),
 * `computeCashflow` dos últimos 365 dias por MONTH e
 * `searchFinanceEntries` (5 mais recentes). Todas de leitura, shim
 * `requireSuperAdmin` (qualquer papel admin, `docs/permissions.md`). Nomes
 * das contas por `subscribeFinanceAccounts`. Sem escrita; atalhos navegam
 * para as outras telas do Financeiro.
 *
 * Usado por: `routes/router.tsx` (rota `finance`, dentro do
 * `RequireSuperAdmin`), menu Financeiro → "Visão geral" (`AdminLayout`).
 * Só `apps/admin` (web). Spec: `docs/screens/finance-overview-admin.md`.
 *
 * Arquitetura: tudo agregado no servidor (Admin SDK sobre
 * `finance_entries`/`finance_accounts`); a tela só compõe.
 *
 * Dados: `ComputeFinanceBalanceResult`, `ComputeCashflowResult` e
 * `FinanceEntry` (`packages/types/src/finance.ts`), valores em CENTAVOS
 * por moeda. O "mês corrente" é o bucket `AAAA-MM` em UTC. Runway = saldo ÷
 * queima líquida média dos meses com lançamento (`null` sem queima).
 *
 * Dicas: `fmtDate` usa o fuso do navegador, enquanto o mês corrente é UTC —
 * perto da virada do mês os dois podem divergir.
 *
 * Issues: #76 (origem).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function FinanceOverviewPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [balance, setBalance] = useState<ComputeFinanceBalanceResult | null>(null);
  const [cashflow, setCashflow] = useState<ComputeCashflowResult | null>(null);
  const [recent, setRecent] = useState<FinanceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const unsub = subscribeFinanceAccounts((r) => {
      if (r.kind === 'READY') setAccounts(r.accounts);
    });
    return (): void => {
      unsub();
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    const now = serverNow();
    Promise.all([
      computeFinanceBalanceCallable({}),
      computeCashflowCallable({ dateFrom: now - 365 * DAY, dateTo: now, granularity: 'MONTH' }),
      searchFinanceEntriesCallable({ page: 1, pageSize: 5 }),
    ])
      .then(([b, c, s]) => {
        if (!alive) return;
        setBalance(b);
        setCashflow(c);
        setRecent(s.items);
      })
      .catch((err: unknown) => {
        console.error('FinanceOverview load failed', err);
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return (): void => {
      alive = false;
    };
  }, []);

  const accountNameById = useMemo(
    () => new Map(accounts.map((a) => [a.id, a.name])),
    [accounts],
  );
  const monthKey = currentMonthKey(serverNow());
  const monthBucket = cashflow?.buckets.find((b) => b.periodKey === monthKey) ?? null;

  /** `DD/MM/AAAA` no fuso do navegador. */
  function fmtDate(ms: number): string {
    const d = new Date(ms);
    const p = (n: number): string => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${String(d.getFullYear())}`;
  }

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('admin.finance.overview.subtitle')}>
          {t('admin.finance.overview.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && <Alert type="error">{t('admin.finance.analysis.errorLoad')}</Alert>}
        {(balance?.capped === true || cashflow?.capped === true) && (
          <Alert type="warning">{t('admin.finance.analysis.capped')}</Alert>
        )}

        {(balance?.byCurrency ?? []).map((c) => {
          const monthNet = monthBucket?.totalsByCurrency[c.currency] ?? null;
          const runway = cashflow ? runwayMonths(c.balanceCents, cashflow.buckets, c.currency) : null;
          return (
            <Container key={c.currency} header={<Header variant="h3">{c.currency}</Header>}>
              <ColumnLayout columns={4} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">{t('admin.finance.overview.kpi.balance')}</Box>
                  <Box
                    variant="awsui-value-large"
                    color={c.balanceCents < 0 ? 'text-status-error' : 'text-status-success'}
                  >
                    {formatCurrencyCents(c.balanceCents, c.currency)}
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">{t('admin.finance.overview.kpi.monthIn')}</Box>
                  <Box variant="awsui-value-large" color="text-status-success">
                    {formatCurrencyCents(monthNet?.inCents ?? 0, c.currency)}
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">{t('admin.finance.overview.kpi.monthOut')}</Box>
                  <Box variant="awsui-value-large" color="text-status-error">
                    {formatCurrencyCents(monthNet?.outCents ?? 0, c.currency)}
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">{t('admin.finance.overview.kpi.runway')}</Box>
                  <Box variant="awsui-value-large">
                    {runway === null
                      ? '—'
                      : t('admin.finance.overview.kpi.runwayMonths', { count: Math.floor(runway) })}
                  </Box>
                </div>
              </ColumnLayout>
            </Container>
          );
        })}

        {(balance?.byCurrency ?? []).map((c) => {
          const data = (cashflow?.buckets ?? [])
            .filter((b) => b.totalsByCurrency[c.currency] !== undefined)
            .map((b) => ({ x: b.periodKey, y: b.totalsByCurrency[c.currency]?.netCents ?? 0 }));
          if (data.length === 0) return null;
          return (
            <Container
              key={`chart-${c.currency}`}
              header={<Header variant="h3">{t('admin.finance.overview.cashflowChart', { currency: c.currency })}</Header>}
            >
              <BarChart
                xScaleType="categorical"
                height={220}
                hideFilter
                hideLegend
                ariaLabel={t('admin.finance.overview.cashflowChart', { currency: c.currency })}
                i18nStrings={{ yTickFormatter: (v): string => formatCurrencyCents(v, c.currency) }}
                series={[
                  {
                    title: t('admin.finance.analysis.columns.net'),
                    type: 'bar',
                    data,
                    valueFormatter: (v): string => formatCurrencyCents(v, c.currency),
                  },
                ]}
                empty={
                  <Box textAlign="center" color="inherit">
                    {t('admin.finance.analysis.empty')}
                  </Box>
                }
              />
            </Container>
          );
        })}

        <Table<FinanceAccountBalance>
          items={balance?.byAccount ?? []}
          loading={loading}
          trackBy="accountId"
          variant="container"
          header={
            <Header
              variant="h3"
              actions={
                <Link
                  onFollow={(e): void => {
                    e.preventDefault();
                    void navigate('/finance/balance');
                  }}
                >
                  {t('admin.finance.overview.seeBalance')}
                </Link>
              }
            >
              {t('admin.finance.overview.byAccount')}
            </Header>
          }
          empty={
            <Box textAlign="center" color="text-status-inactive" padding="m">
              {t('admin.finance.analysis.empty')}
            </Box>
          }
          columnDefinitions={[
            {
              id: 'account',
              header: t('admin.finance.analysis.columns.account'),
              cell: (a): React.ReactNode => accountNameById.get(a.accountId) ?? a.accountId,
            },
            {
              id: 'currency',
              header: t('admin.finance.analysis.columns.currency'),
              cell: (a): React.ReactNode => a.currency,
            },
            {
              id: 'balance',
              header: t('admin.finance.overview.kpi.balance'),
              cell: (a): React.ReactNode => (
                <SignedCents cents={a.balanceCents} currency={a.currency} />
              ),
            },
          ]}
        />

        <TagBreakdownTable
          items={balance?.byTag ?? []}
          title={t('admin.finance.overview.byTag')}
        />

        <Table<FinanceEntry>
          items={recent}
          loading={loading}
          trackBy="id"
          variant="container"
          header={
            <Header
              variant="h3"
              actions={
                <Link
                  onFollow={(e): void => {
                    e.preventDefault();
                    void navigate('/finance/entries');
                  }}
                >
                  {t('admin.finance.overview.seeEntries')}
                </Link>
              }
            >
              {t('admin.finance.overview.recent')}
            </Header>
          }
          empty={
            <Box textAlign="center" color="text-status-inactive" padding="m">
              {t('admin.finance.analysis.empty')}
            </Box>
          }
          columnDefinitions={[
            {
              id: 'date',
              header: t('admin.finance.analysis.columns.date'),
              cell: (e): React.ReactNode => fmtDate(e.date),
            },
            {
              id: 'description',
              header: t('admin.finance.analysis.columns.description'),
              cell: (e): React.ReactNode => e.description,
            },
            {
              id: 'amount',
              header: t('admin.finance.analysis.columns.net'),
              cell: (e): React.ReactNode => (
                <Box
                  textAlign="right"
                  color={e.type === 'OUT' ? 'text-status-error' : 'text-status-success'}
                >
                  {e.type === 'OUT' ? '-' : ''}
                  {formatCurrencyCents(e.amountCents, e.currency)}
                </Box>
              ),
            },
          ]}
        />
      </SpaceBetween>
    </ContentLayout>
  );
}
